import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { BarChart3, FileSpreadsheet, Handshake, Printer, Target } from 'lucide-react';
import type { ReportList } from '@mnc/contracts';
import { api } from '../../lib/api';
import { pick } from '../../i18n';
import { Badge, EmptyState, PageHeader, TableSkeleton } from '../../components/ui';

const ICONS: Record<string, typeof Target> = { strategy: Target, programs: Handshake };

/** The hub: one card per report in the registry that this instance has enabled and the reader may open. */
export function ReportsPage() {
  const { t, i18n } = useTranslation();
  const list = useQuery({ queryKey: ['reports'], queryFn: () => api.get<ReportList>('/api/reports') });
  const items = list.data?.items ?? [];
  return (
    <div>
      <PageHeader title={t('reports.pageTitle')} subtitle={t('reports.subtitle')} />
      {list.isLoading ? (
        <TableSkeleton />
      ) : items.length === 0 ? (
        <EmptyState>{t('reports.none')}</EmptyState>
      ) : (
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
          {items.map((r) => {
            const Icon = ICONS[r.icon] ?? BarChart3;
            return (
              <Link key={r.code} to={r.route} className="card block p-4 transition hover:border-brand-500 hover:shadow-sm">
                <div className="mb-2 flex items-center gap-2">
                  <span className="rounded-lg bg-brand-50 p-2 text-brand-700 dark:bg-brand-700/20 dark:text-brand-100"><Icon size={18} /></span>
                  <h2 className="font-semibold" dir="auto">{pick(i18n.language, r.name_en, r.name_ar)}</h2>
                </div>
                <p className="mb-3 text-sm text-neutral-600 dark:text-neutral-300" dir="auto">{pick(i18n.language, r.description_en, r.description_ar)}</p>
                <div className="flex flex-wrap items-center gap-1.5 text-xs">
                  {r.formats.includes('print') && <Badge tone="neutral"><Printer size={12} className="me-1 inline" />{t('reports.formats.print')}</Badge>}
                  {r.formats.includes('xlsx') && <Badge tone={r.can_export ? 'brand' : 'neutral'}><FileSpreadsheet size={12} className="me-1 inline" />{t(r.can_export ? 'reports.formats.xlsx' : 'reports.formats.xlsxNoRight')}</Badge>}
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
